// src/store/socket.js
import { defineStore } from "pinia";
import { io } from "socket.io-client";
import { showToast } from "@/utils/toast";
import { useConfigStore } from "@/store/config";
import { useDialogStore } from "@/store/dialog";

export const useSocketStore = defineStore("socket", {
  state: () => ({
    socket: null,
    isConnected: false,
    isLoggedIn: false, // 新增：登入狀態
    loginPromise: null, // 新增：登入 Promise
    error: null,
    tableId: "",
    listeners: new Map(),
    events: new EventTarget(),
  }),
  getters: {
    getIsConnected: (state) => state.isConnected,
    getIsLoggedIn: (state) => state.isLoggedIn,
    getIsReady: (state) => state.isConnected && state.isLoggedIn,
  },
  actions: {
    trackSocket(type, dataInfo) {
      if (window._paq) {
        window._paq.push([
          "trackEvent",
          "socket",
          type,
          dataInfo, // info
          Date.now(),
        ]);
      }
    },
    // 連接 Socket.IO
    // 使用模組級單例以避免重複建立連線（跨多個 import/hmr 保持唯一）
    connect(url, options = {}) {
      if (
        typeof globalThis.__GLOBAL_SOCKET_INSTANCE__ !== "undefined" &&
        globalThis.__GLOBAL_SOCKET_INSTANCE__
      ) {
        this.socket = globalThis.__GLOBAL_SOCKET_INSTANCE__;
      }
      this.trackSocket("socket_connect_start", `socketID:${this.socket?.id}`);
      const configStore = useConfigStore();
      const dialogStore = useDialogStore();
      let retryCount = 0;
      if (this.socket) {
        // 如果已有 socket 實例，避免再建立新的連線
        console.warn(
          "Socket exists, connecting state =",
          this.socket.connected,
        );
        // 已連線則直接回傳
        if (this.socket.connected) {
          console.warn("Socket.IO already connected");
          return;
        }
        // 若正在連線中則重用現有實例
        if (this.socket.connecting) {
          console.warn("Socket.IO is already connecting");
          return;
        }
        // 嘗試使用現有 socket 進行 connect（重用實例）
        try {
          this.socket.connect?.();
          return;
        } catch (err) {
          console.warn("Failed to reuse existing socket, recreating", err);
        }
      }
      try {
        const defaultOptions = {
          autoConnect: true,
          reconnection: true,
          reconnectionAttempts: 5,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          transports: ["websocket"],
          ...options,
        };

        // === 建立 Socket.IO 連線 ===
        this.socket = io(url, defaultOptions);
        // 設定全局單例引用，避免其他模組/組件重複建立
        console.log("socket ", this.socket.id);
        // this.trackSocket("socket_connect_start", `socketID:${this.socket.id}`);
        try {
          globalThis.__GLOBAL_SOCKET_INSTANCE__ = this.socket;
        } catch (e) {}

        // === 已連線 ===
        this.socket.on("connect", async () => {
          this.trackSocket(
            "socket_connect_end",
            `socketID:${this.socket?.id},status:"success"`,
          );
          this.isConnected = true;
          retryCount = 0;
          // 自動執行登入流程
          if (configStore.getToken) {
            await this._performLogin(configStore.getToken);
          } else {
            console.warn(
              "No token found in config store during socket connect",
            );
          }
          await this.events.dispatchEvent(new Event("connected"));
        });

        // === 斷線 ===
        this.socket.on("disconnect", (reason) => {
          console.log("❗ disconnect:", reason);
          this.trackSocket(
            "socket_disconnect",
            `socketID:${this.socket?.id},status:success,reason:${reason}`,
          );
          this.isConnected = false;
          this.isLoggedIn = false; // 斷線時重置登入狀態
          this.loginPromise = null;
        });

        // === 連線錯誤 ===
        this.socket.on("connect_error", (error) => {
          console.error("❌ connect_error:", error.message);
          this.trackSocket("socket_connect_error", `error:${error}`);
          this.error = error.message;
        });

        // === loginResponse ===
        this.socket.on("loginResponse", (data) => {
          console.log("🔐 socket loginResponse:", data);
          this.trackSocket(
            "socket_login_on",
            `socketID:${this.socket?.id},status:${data.success == true ? "success" : "fail"}`,
          );
          if (data.success) {
            this.isLoggedIn = true;
            this.events.dispatchEvent(new Event("loginSuccess"));
            configStore.userLocation = "lobby";
          } else {
            console.log("socket:", data);
            this.isLoggedIn = false;
            // 觸發一次性的失敗事件
            this.events.dispatchEvent(
              new CustomEvent("loginFailed", { detail: data }),
            );
            // 主動斷線並清理全域實例
            try {
              dialogStore.showDialog(
                "请重新登入继续<br/>登录凭证已失效，Socket 连接被拒绝。",
                "logout",
                "inactive",
              );
              console.log("socket disconnect");
              // this.disconnect();
            } catch (e) {
              console.warn("disconnect on login failed failed", e);
            }
          }
        });

        // === logout 全局監聽 ===
        this.socket.on("logout", () => {
          // 觸發全局事件
          this.events.dispatchEvent(new CustomEvent("logout"));
        });
        // === 重連機制 ===
        this.socket.io.on("reconnect_attempt", (attempt) => {
          this.trackSocket(
            "socket_reconnect_start",
            `socketID:${this.socket?.id},count: ${attempt}`,
          );
          retryCount = attempt;
          console.log(`🔄 Reconnecting... attempt ${attempt}/5`);
        });

        this.socket.io.on("reconnect", async (attempt) => {
          this.trackSocket(
            "socket_reconnect_end",
            `socketID:${this.socket?.id},status:success,count: ${attempt}`,
          );
          console.log("=== RECONNECT === attempt", attempt);
          console.log(`🟢 Reconnected after ${attempt} attempts`);
          retryCount = 0;
        });

        this.socket.io.on("reconnect_failed", async () => {
          this.trackSocket(
            "socket_reconnect_end",
            `socketID:${this.socket?.id},status:fail`,
          );
          console.error("❌ Reconnect failed");
          // 通知socket斷線
          this.events.dispatchEvent(new CustomEvent("disconnected"));
          await dialogStore.showDialog(
            "请重新登入继续!!",
            "logout",
            "inactive",
          );
        });
      } catch (err) {
        this.error = err.message;
        console.error("Failed to create Socket.IO:", err);
      }
    },
    async _performLogin(token) {
      const configStore = useConfigStore();
      // ✅ 如果正在登入,等待結果(避免重複登入)
      if (this.loginPromise) {
        console.log("⏳ 已有登入進行中,等待結果");
        return this.loginPromise;
      }

      // ✅ 確保 socket 已連接
      if (!this.socket?.connected) {
        console.log("⏳ Socket 未連接,等待連接...");
        try {
          await this.waitForConnect();
          console.log("✅ Socket 已連接");
        } catch (error) {
          console.error("❌ 等待連接失敗:", error);
          throw error;
        }
      }

      console.log("🔐 開始登入流程 socket");
      // 追蹤socket login emit
      this.trackSocket(
        "socket_login_emit",
        `socketID:${this.socket.id},token:${token}`,
      );

      this.loginPromise = new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          console.error("❌ 登入超時");
          this.trackSocket(
            "socket_login_on",
            `socketID:${this.socket.id},token:${token},error:連線超時`,
          );
          cleanup();
          reject(new Error("Login timeout"));
          configStore.logout();
        }, 10000); // 到 10 秒

        const cleanup = () => {
          clearTimeout(timeout);
          this.events.removeEventListener("loginSuccess", onSuccess);
          this.events.removeEventListener("loginFailed", onFailed);
          this.loginPromise = null;
        };

        const onSuccess = () => {
          console.log("✅ 登入成功");
          cleanup();
          this.isLoggedIn = true;
          resolve(true);
        };

        const onFailed = (e) => {
          console.error("❌ 登入失敗:", e.detail?.message);
          cleanup();
          this.isLoggedIn = false;
          reject(new Error(e.detail?.message || "Login failed"));
        };
        this.events.addEventListener("loginSuccess", onSuccess, { once: true });
        this.events.addEventListener("loginFailed", onFailed, { once: true });

        // ✅ 確保 socket 連接後才 emit
        if (this.socket?.connected) {
          this.socket.emit("login", {
            token,
            visitor_id: configStore.matomoVisitorId ?? null,
          });
        } else {
          cleanup();
          this.trackSocket(
            "socket_login_emit",
            `socketID:${this.socket.id},token:${token},error:Socket not connected`,
          );
          reject(new Error("Socket not connected"));
        }
      });
      return this.loginPromise;
    },

    // 等待登入完成
    async waitForLogin(timeout = 100000) {
      if (this.isLoggedIn) {
        return true;
      }
      const dialogStore = useDialogStore();
      const configStore = useConfigStore();
      return new Promise((resolve, reject) => {
        const timeoutId = setTimeout(async () => {
          this.events.removeEventListener("loginSuccess", handleSuccess);
          this.events.removeEventListener("loginFailed", handleFailed);
          showToast(
            "message:Socket Wait for login timeout",
            "danger",
            3000,
            true,
          );
          reject(new Error("Wait for login timeout"));
        }, timeout);

        const handleSuccess = () => {
          clearTimeout(timeoutId);
          this.events.removeEventListener("loginSuccess", handleSuccess);
          this.events.removeEventListener("loginFailed", handleFailed);
          resolve(true);
        };

        const handleFailed = async (event) => {
          clearTimeout(timeoutId);
          this.events.removeEventListener("loginSuccess", handleSuccess);
          this.events.removeEventListener("loginFailed", handleFailed);
          await dialogStore.showDialog("请重新登入继续", "logout", "inactive");
          reject(new Error(event.detail?.message || "Login failed"));
        };

        this.events.addEventListener("loginSuccess", handleSuccess);
        this.events.addEventListener("loginFailed", handleFailed);
      });
    },
    // 等待 connect 完成
    waitForConnect() {
      return new Promise((resolve, reject) => {
        if (this.socket?.connected) {
          resolve();
          return;
        }

        const onConnect = () => {
          this.socket.off("connect_error", onError);
          resolve();
        };

        const onError = (err) => {
          this.socket.off("connect", onConnect);
          reject(err);
        };

        this.socket.once("connect", onConnect);
        this.socket.once("connect_error", onError);
      });
    },

    // 發送事件（需要先登入）
    async emit(event, data, callback) {
      if (!this.socket || !this.socket.connected) {
        console.error("Socket.IO not connected emit:", event, data);
        if (callback) callback({ error: "Not connected" });
        return false;
      }
      // 登入事件不需要檢查登入狀態
      if (event !== "login") {
        console.log("socket emit", event, data);
        if (!this.isLoggedIn) {
          console.warn(
            `⚠️ Attempting to emit [${event}] before login completed`,
          );
          try {
            // 等待登入完成
            await this.waitForLogin();
            console.log(`✅socket Login completed, now emitting [${event}]`);
          } catch (error) {
            console.error(
              `❌✅socket Login failed, cannot emit [${event}]`,
              error,
            );
            if (callback) callback({ error: "Not logged in" });
            return false;
          }
        }
      } else {
        // 追蹤socket login emit從登入頁開始
        this.trackSocket(
          `socket_login_emit`,
          `socketID:${this.socket?.id},token:${data.token}`,
        );
      }

      try {
        if (callback && typeof callback === "function") {
          this.socket.emit(event, data, callback);
          console.log(`📤 socket Emit [${event}] with callback:`, data);
        } else {
          this.socket.emit(event, data);
          console.log(`📤socket Emit [${event}]:`, data);
        }
        return true;
      } catch (error) {
        console.error("socket Failed to emit:", error);
        if (callback) callback({ error: error.message });
        return false;
      }
    },

    // 監聽事件
    on(event, callback) {
      if (!this.socket) {
        console.error("Socket.IO not initialized on:", event);
        return;
      }
      if (!this.listeners.has(event)) {
        this.listeners.set(event, []);
      }
      this.listeners.get(event).push(callback);
      this.socket.on(event, callback);
      console.log(`👂socket Listening to [${event}]`);
    },

    // 移除監聽
    off(event, callback) {
      if (!this.socket) return;

      if (callback) {
        this.socket.off(event, callback);

        if (this.listeners.has(event)) {
          const callbacks = this.listeners.get(event);
          const index = callbacks.indexOf(callback);
          if (index > -1) {
            callbacks.splice(index, 1);
          }
        }
      } else {
        this.socket.off(event);
        this.listeners.delete(event);
      }

      console.log(`🔇 Stopped listening to [${event}]`);
      // //登出後 直接斷掉 socket
      // this.socket.disconnect();
    },

    // 一次性監聽
    once(event, callback) {
      if (!this.socket) {
        console.error("Socket.IO not initialized");
        return;
      }
      this.socket.once(event, callback);
      console.log(`👂 Listening once to [${event}]`);
    },

    // 斷開連接
    disconnect() {
      if (this.socket) {
        this.socket.disconnect();
        this.socket = null;
        this.isConnected = false;
        this.isLoggedIn = false;
        this.loginPromise = null;
        this.listeners.clear();
        // 通知socket斷線
        this.events.dispatchEvent(new CustomEvent("disconnected"));
        console.log("🔌 Socket.IO disconnected");
        try {
          globalThis.__GLOBAL_SOCKET_INSTANCE__ = null;
        } catch (e) {
          // ignore
        }
      }
    },
  },
});
